/* eslint-disable max-len */
import { graphql as coreGraphqlTypes } from '@sitebuilder/core';
import * as types from './types';
import * as viewModelTypes from './viewModelTypes';
import * as qglQuery from './graphqlQuery';

// export * as types from './types';
// export * as query from './graphqlQuery';
// export * as viewModelTypes from './viewModelTypes';
export { types, viewModelTypes, qglQuery };

const buildQuery = () => {
    const { query, transform } = qglQuery;
    return { query, transform };
};

const transformData = (
    { data }: types.FundListResponse,
    locale: string
): viewModelTypes.FundListViewModel => {
    if (!data) {
        return {
            locale: '',
            webProfiles: [],
            annotations: [],
            documents: [],
        };
    }

    const webProfiles: viewModelTypes.WebProfile[] = [];

    data.webProfiles?.forEach((webProfile) => {
        const overview: viewModelTypes.WebProfileOverviewModel = {};

        const primaryProfileSalesOption = (webProfile.webProfileSalesOptions || [])[0] || {};
        const primarySalesOption = primaryProfileSalesOption.salesOption || {};
        const primarySeries = primarySalesOption.series || {};
        const fund = primarySeries.fund || {};
        const fees = (primarySeries.fees || [])[0] || {};
        const relatedDocuments = webProfile.documents;

        overview.fundName = fund.fullName;
        overview.seriesName = primarySeries.seriesType?.name;
        overview.webProfileFundName = webProfile.fundName;
        overview.seriesShortName = primarySeries.seriesType?.shortName;
        overview.seriesSortOrder = primarySeries.seriesType?.sortOrder;
        overview.aum = fund.aum;
        overview.riskRatingName = fund.riskRating?.name;
        overview.riskRating = fund.riskRating;
        overview.assetClass = fund.assetClass?.name;
        overview.assetClassSortOrder = fund.assetClass?.sortOrder;
        overview.cifscCategory = fund.cifscCategory?.name;
        overview.fundCategoryName = fund.fundCategory?.name;
        overview.fundCategory = fund.fundCategory;
        overview.inceptionDate = primarySeries.inceptionDate;
        overview.mer = fees.mer;
        overview.currency = primarySeries.currency?.iso4217alpha || '';

        const fundCodes: Record<string, string | null> = {};
        const currentSeries = fund.series?.find((s) => s.entityId === webProfile.entityId);
        let productName: string | null = null;
        let productSortOrder: number | undefined;
        let productGroupSortOrder: number | undefined;

        if (currentSeries) {
            currentSeries.fundCodes?.forEach((x) => {
                if (x.loadType?.entityId) {
                    fundCodes[x.loadType.entityId] = x.entityId || null;
                }

                if (!productName && x.productGroup?.name && x.product?.name) {
                    productName = `${x.productGroup?.name} - ${x.product?.name}`;
                    productSortOrder = x.product.sortOrder;
                    productGroupSortOrder = x.productGroup.sortOrder;
                } else if (!productName && x.productGroup?.name) {
                    productName = x.productGroup?.name;
                    productGroupSortOrder = x.productGroup.sortOrder;
                } else if (!productName && x.product?.name) {
                    productSortOrder = x.product.sortOrder;
                    productName = x.product?.name;
                }
            });
        }

        if (!productName && fund.productGroup?.name && fund.product?.name) {
            productName = `${fund.productGroup?.name} - ${fund.product.name}`;
            productSortOrder = fund.product.sortOrder;
            productGroupSortOrder = fund.productGroup.sortOrder;
        } else if (!productName && fund.productGroup?.name) {
            productName = fund.productGroup.name;
            productGroupSortOrder = fund.productGroup.sortOrder;
        } else if (!productName && fund.product?.name) {
            productSortOrder = fund.product.sortOrder;
            productName = fund.product.name;
        }

        overview.fundCodes = fundCodes;
        overview.productName = productName;
        overview.productSortOrder = productSortOrder;
        overview.productGroupSortOrder = productGroupSortOrder;

        let performance: viewModelTypes.PerformanceViewModel | undefined;
        const compoundPerformance = (primarySalesOption.compoundPerformance || [])[0] || null;

        if (primarySalesOption._performance_dailyPerformance) {
            performance = {
                dailyPerformance: primarySalesOption._performance_dailyPerformance,
            };
        }

        if (compoundPerformance && compoundPerformance.p1yr) {
            performance = {
                ...(performance || {}),
                effectiveDate: compoundPerformance.effectiveDate,
            };

            if (compoundPerformance) {
                performance.compoundPerformance = compoundPerformance;
            }
        }

        const morningStarSeriesRatingTypes = {
            overAllRating: 'overAllNumFund',
            _1moRating: 'numFund1mo',
            _3moRating: 'numFund3mo',
            _6moRating: 'numFund6mo',
            _1yrRating: 'numFund1yr',
            _3yrRating: 'numFund3yr',
            _5yrRating: 'numFund5yr',
            _10yrRating: 'numFund10yr',
        };

        let morningStarSeriesRatings: viewModelTypes.MorningStarSeriesRating | null = null;

        if (primarySeries.morningStarSeriesRatings) {
            morningStarSeriesRatings = {};
            const morningStarData = primarySeries?.morningStarSeriesRatings[0] || {};

            if (morningStarData) {
                Object.keys(morningStarSeriesRatingTypes).forEach((k) => {
                    // eslint-disable-next-line
                    (morningStarSeriesRatings as any)[k] = {
                        // eslint-disable-next-line
                        rating: (morningStarData as any)[k],
                        // eslint-disable-next-line
                        numFund: (morningStarData as any)[(morningStarSeriesRatingTypes as any)[k]],
                    };
                });

                morningStarSeriesRatings.effectiveDate = morningStarData.effectiveDate;
            }
        }

        if (webProfile.isInactive !== true) {
            webProfiles.push({
                entityId: webProfile.entityId,
                sortOrder: webProfile.sortOrder,
                isInactive: webProfile.isInactive,
                overview,
                performance,
                morningStarSeriesRatings,
                relatedDocuments,
            });
        }
    });

    const annotations = [
        ...(data.globalWebProfileAnnotations || []),
        ...(data.globalFundAnnotations || []),
        ...(data.globalSeriesAnnotations || []),
    ];

    return { locale, webProfiles, annotations, documents: data.globalDocument };
};

export const fetchFundListData = async (
    dataSource: string,
    variables: Record<string, string>,
    gqlClient: coreGraphqlTypes.IGraphqlClient
) => {
    const { query, transform } = buildQuery();

    const extensions: Record<string, unknown> = {};

    if (transform) {
        extensions.transform = transform;
    }

    const params = {
        query: query || '',
        extensions,
        variables,
    };

    const defaultLocale = 'en-US';
    let locale = params.variables.locale;

    if (typeof params.variables.locale !== 'string' || params.variables.locale.length < 1) {
        locale = params.variables.locale = defaultLocale;
    }

    const result = await gqlClient.query<types.FundListResponse>(dataSource, params);
    return transformData(result || { data: {} }, locale);
};
